import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import reduxUtils from 'util/redux';
import classnames from 'classnames';
import Button from 'components/button';
import Icon from 'components/icon';
import Loader from 'components/loader';
import Cart from 'components/cart';
import t, { Trans } from 'translations';
import { MAX_ITEMS } from 'constants/cart';
import { all } from 'util/dom/handle-accessible-key-events';
import FormFieldSelect from 'components/form/form-field-select';

const MORE_VALUE = 'more';
const REMOVE_VALUE = 'remove';

class BuyBox extends Component {
    static defaultProps = {
        facet: 'base',
        onBought: () => {
        },
    };

    static propTypes = {
        facet: PropTypes.string,
        fetchingId: PropTypes.string,
        isCartReady: PropTypes.bool.isRequired,
        isFetching: PropTypes.bool.isRequired,
        product: PropTypes.object.isRequired,
        activeRegion: PropTypes.object,
        items: PropTypes.object.isRequired,
        isModalVisible: PropTypes.bool.isRequired,
        addProduct: PropTypes.func.isRequired,
        togglePanel: PropTypes.func.isRequired,
        setProductAmount: PropTypes.func.isRequired,
        onBought: PropTypes.func,
    };

    state = {
        isReady: !!window.styleguide,
        amount: 1,
        autoFocus: false,
        pendingSelectShow: false,
        showSelect: false,
    };

    componentDidMount() {
        this.syncAmount(this.props);
        this.setReadyState(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.syncAmount(nextProps);

        if (
            this.state.pendingSelectShow && !nextProps.isFetching ||
            !this.props.items[this.props.product.id] && nextProps.items[this.props.product.id]
        ) {
            this.setState({ pendingSelectShow: false, showSelect: true });
        }

        this.setReadyState(nextProps);
    }

    componentDidUpdate() {
        if (this.state.autoFocus) {
            this.refs.input.focus();
            this.setState({ autoFocus: false });
        }
    }

    onButtonClick = () => {
        const { product, onBought } = this.props;
        const { amount } = this.state;
        this.props.setProductAmount({ product, amount });

        if (this.props.activeRegion) {
            this.setState({ pendingSelectShow: true });
        }

        onBought();
    };

    onChange = (e) => {
        const value = +e.currentTarget.value;

        if (typeof value === 'number' && value >= 0) {
            this.setState({ amount: value });
        }
    };

    onSelectChange = (value) => {
        const { onBought } = this.props;

        switch (value) {
            case REMOVE_VALUE:
                this.removeProduct();
                break;
            case MORE_VALUE:
                this.setState({
                    amount: null,
                    showSelect: false,
                    autoFocus: true,
                });
                break;
            default:
                this.props.setProductAmount({ product: this.props.product, amount: +value });
        }

        onBought();
    };

    setReadyState(props) {
        // toggle the select on init
        if (props.isCartReady && !this.state.isReady) {
            this.setState({
                showSelect: props.items[props.product.id],
                isReady: true,
            });
        }
    }

    decrease = () => {
        if (this.state.amount > 0) {
            this.setState({ amount: this.state.amount - 1 });
        }
    };

    increase = () => {
        this.setState({ amount: this.state.amount + 1 });
    };

    openCart() {
        this.props.togglePanel({ facet: 'cart', content: <Cart/> });
    }

    removeProduct = () => {
        const { fetchingId, product, onBought, setProductAmount } = this.props;
        const isLoading = fetchingId === product.id;

        if (isLoading) {
            return;
        }

        setProductAmount({ product, amount: 0 });
        this.setState({ showSelect: false });
        onBought();
    };

    get selectOptions() {
        const { product, items } = this.props;
        const amount = items[product.id] ? items[product.id].amount : this.state.amount;

        const options = Array.from({ length: 9 }, (e, i) => {
            const value = i + 1;
            let label;

            if (amount === value) {
                label = t('buy_box.items_in_the_cart', { amount: value });
            } else {
                label = String(i + 1);
            }

            return {
                label,
                value: String(value),
            };
        });

        if (amount > options.length) {
            options.splice(0, 0, { value: String(amount), label: t('buy_box.items_in_the_cart', { amount }) });
        }

        options.push({ label: `10+`, value: MORE_VALUE });
        options.push({ label: t('buy_box.remove'), value: REMOVE_VALUE }); // add an extra item for design reasons

        return options;
    }

    syncAmount({ items, product }) {
        const cartItem = items[product.id];
        const oldCartItem = this.props.items[product.id];
        const amount = cartItem ? cartItem.amount : this.state.amount;

        if (!cartItem && this.state.showSelect) {
            this.setState({ showSelect: false, amount: 1 });
        } else if (!oldCartItem || !cartItem || oldCartItem.amount !== cartItem.amount) {
            this.setState({ amount });
        }
    }

    renderFooterComponents() {
        const { showSelect } = this.state;

        return (
            <div className={ classnames('buy-box--footer', { 'buy-box--footer__has-select': showSelect }) }>
                <Button onClick={ () => this.openCart() }
                    className="buy-box--link-to-cart u-reset">
                    <Trans path="buy_box.to_cart"/>
                </Button>
            </div>
        );
    }

    renderHeaderComponents() {
        const { fetchingId, product, items } = this.props;
        const { amount, showSelect } = this.state;
        const isLoading = fetchingId === product.id;
        const selectOptions = this.selectOptions;
        const selectedOption = items[product.id] ? selectOptions.find(({ value }) => value === String(items[product.id].amount)) : null;

        return (
            <div className={ classnames('buy-box--header', { 'buy-box--header__has-select': showSelect }) }>

                <div className="buy-box--header-content">
                    <div aria-hidden={ showSelect } className="buy-box--panel">
                        <Button onClick={ this.decrease } className="buy-box--lateral-button"
                            aria-label={ t('accessibility.label.remove_one', {
                                product_name: product.name,
                            }) }>
                            <Icon symbol="basket-remove" size="large"/>
                        </Button>
                        <div className="buy-box--form-field">
                            <input aria-label={ t('accessibility.label.product_count') } ref="input"
                                onKeyUp={ e => all(e) && amount && this.onButtonClick() }
                                className="buy-box--input" max={ MAX_ITEMS } type="number"
                                onChange={ this.onChange }
                                value={ amount ? amount.toFixed(0) : '' }/>
                        </div>
                        <Button onClick={ this.increase } className="buy-box--lateral-button"
                            aria-label={ t('accessibility.label.add_to_cart', {
                                product_name: product.name,
                            }) }>
                            <Icon symbol="basket-add" size="large"/>
                        </Button>
                    </div>
                    <div className="buy-box--elevator">
                        { showSelect ? <div className="buy-box--select-wrapper">
                            <FormFieldSelect
                                onInput={ this.onSelectChange }
                                value={ selectedOption ? selectedOption.value : selectOptions[0].value }
                                facet="buybox"
                                items={ selectOptions }/>

                            <Button
                                aria-label={ t('accessibility.label.delete_product') }
                                facet="primary"
                                className="buy-box--delete-button"
                                hasFocus={ isLoading }
                                onClick={ this.removeProduct }>
                                { isLoading ? <Loader size="small"/> :
                                    <Icon symbol="trash" size="medium"/> }
                            </Button>
                        </div> : <Button
                            aria-label={ t('accessibility.label.add_to_cart', {
                                product_name: product.name,
                            }) }
                            disabled={ !amount }
                            onClick={ this.onButtonClick }
                            className="buy-box--add-to-cart u-reset"
                            size="large"
                            facet="primary">
                            { fetchingId ? '' : <div className="buy-box--button-text">
                                <Trans path="cart.add_to_cart"/>
                            </div> }
                            { fetchingId === product.id ? <div className="buy-box--button-loader">
                                <Loader size="small"/>
                            </div> : null }
                        </Button>
                        }
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { isReady } = this.state;

        if (!isReady) {
            return <Loader/>;
        }

        return (
            <div className={ classnames('buy-box', `buy-box__${ this.props.facet }`) }>
                { this.renderHeaderComponents() }
                { this.renderFooterComponents() }
            </div>
        );
    }
}


export default connect(
    reduxUtils.mapStateToProps({
        items: 'cart.items',
        isFetching: 'cart.isFetching',
        fetchingId: 'cart.fetchingId',
        isCartReady: 'cart.wasFetched',
        isModalVisible: 'modal.isVisible',
        activeRegion: 'region.active',
    }),
    reduxUtils.mapDispatchToProps({
        addProduct: 'cart.addProduct',
        setProductAmount: 'cart.setProductAmount',
        togglePanel: 'panel.toggle',
    }),
)(BuyBox);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/buy-box/index.js